import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client"; // Import createRoot
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import RouterConfig from "./App";
import { AuthProvider } from "./components/AuthContextComponent";
import {
  CustomAccountProvider,
  useCustomAccount,
} from "./components/CustomAccountContext";
import { accountNumber, baseURl } from "./lib/config/Config";
import { ApiRequest } from "./lib/interfaces/APIRequestInterface";
import { systemUse } from "./lib/constants/Constants";
import { partnerCustomization } from "./api/FetchAPI";
import axios from "axios";
import {
  showAlert,
  showConnectionError,
} from "./promptMessages/PromptMessages";
import { ALERTMSG } from "./lib/enums";
import { VersionProvider } from "./components/VersionContext";
import { BalanceProvider } from "./components/BalanceContext";
import { FetchErr } from "./lib/helpers/UnhandledError";

// Create a new functional component for setting up the app
const App = () => {
  const { customAccount } = useCustomAccount();

  const [customization, setCustomization] = useState<string>("");

  const [customImg, setCustomImg] = useState(() => {
    return sessionStorage.getItem("favicon") || ""; // Retrieve favicon from sessionStorage
  });

  const img = sessionStorage.getItem("favicon") || "";

  useEffect(() => {
    const customAccountFetch = async () => {
      const request: ApiRequest = {
        partnersId: parseInt(accountNumber),
        systemUse: systemUse,
      };
      try {
        const result = await partnerCustomization(request);

        const { respCode, respMsg } = result;

        if (respCode === 2000) {
          setCustomization(result.customAccountInfo[0].partnersId.toString());

          const imgResult = result.customAccountInfo[0].logoImage;

          // Fetch the logo image
          const imgView = await axios.get(
            `${baseURl}/ViewImage?img=${imgResult}`,
            {
              responseType: "arraybuffer",
            }
          );

          const blob = new Blob([imgView.data], { type: "image/png" });

          const imageUrl = URL.createObjectURL(blob);

          setCustomImg(imageUrl);

          sessionStorage.setItem("favicon", imageUrl); // Store the favicon in sessionStorage
        } else {
          showAlert(
            ALERTMSG.SOMEWENTWRONG,
            `${respMsg} (${respCode})`,
            "error",
            result.customAccountInfo[0].themeCode
          );
        }
      } catch (error) {
        FetchErr(error, "", "", "");
      }
    };
    customAccountFetch();
  }, []);

  useEffect(() => {
    const headerLogo = document.getElementById(
      "dynamic-logo"
    ) as HTMLLinkElement;

    if (accountNumber === "0") {
      // Disbursement
      document.title = "Disbursement";
      if (headerLogo) {
        headerLogo.href = img;
      } else {
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = img;
        link.id = "dynamic-logo";
        document.head.appendChild(link);
      }
    } else if (accountNumber === customization) {
      //MetroBank
      document.title = "MetroBank Disbursement";

      if (headerLogo) {
        headerLogo.href = img; // Update the existing link
      } else {
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = img;
        link.id = "dynamic-logo";
        document.head.appendChild(link);
      }
    } else {
      document.title = "Loading...";
      if (headerLogo) {
        headerLogo.href = img;
      }
    }
  }, [customAccount, customization, img]);

  window.onerror = function (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean {
    const errMsg =
      typeof message === "string" ? message : JSON.stringify(message);

    showConnectionError(
      ALERTMSG.UNDHANDLEDERR,
      `${errMsg} (${source || "unknown source"}:${lineno ?? "?"})`,
      "error",
      "",
      ""
    );

    return true;
  };

  window.onunhandledrejection = function (event: PromiseRejectionEvent): void {
    const errorMsg =
      event.reason?.message ||
      (typeof event.reason === "string"
        ? event.reason
        : JSON.stringify(event.reason)) ||
      "Unhandled promise rejection";

    showConnectionError(
      ALERTMSG.UNDHANDLEDERR,
      `(${errorMsg})`,
      "error",
      "",
      ""
    );
  };
  return <RouterConfig />;
};

const container = document.getElementById("root");

if (container) {
  const root = createRoot(container); // Create a root

  root.render(
    // <React.StrictMode>

    <VersionProvider>
      <BalanceProvider>
        <CustomAccountProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </CustomAccountProvider>
      </BalanceProvider>
    </VersionProvider>

    // </React.StrictMode>
  );
}

// Measure performance
reportWebVitals();
